/**
 * Global focus detection utility
 * Detects keyboard vs mouse navigation and toggles .is-tabbing class on body
 *
 * This enables showing focus indicators only during keyboard navigation,
 * improving accessibility without cluttering the UI during mouse interaction.
 */

let isTabbing = false;

/**
 * Handle Tab key press - switch to keyboard mode
 */
function handleKeyDown(e: KeyboardEvent): void {
  if (e.key === 'Tab' && !isTabbing) {
    isTabbing = true;
    document.body.classList.add('is-tabbing');
  }
}

/**
 * Handle mouse down - switch to mouse mode
 */
function handleMouseDown(): void {
  if (isTabbing) {
    isTabbing = false;
    document.body.classList.remove('is-tabbing');
  }
}

/**
 * Initialize global focus detection
 * Call this once at app initialization
 *
 * @returns Cleanup function to remove event listeners
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   const cleanup = initializeFocusDetection();
 *   return cleanup;
 * }, []);
 * ```
 */
export function initializeFocusDetection(): () => void {
  // Add event listeners with passive flag for better performance
  window.addEventListener('keydown', handleKeyDown, { passive: true });
  window.addEventListener('mousedown', handleMouseDown);

  // Return cleanup function
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('mousedown', handleMouseDown);
  };
}

/**
 * Check if currently in keyboard mode
 * Useful for programmatic checks
 *
 * @returns true if currently in keyboard navigation mode
 */
export function isKeyboardMode(): boolean {
  return document.body.classList.contains('is-tabbing');
}
